import { useState, useEffect, useCallback } from 'react';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';
import { X, Lock, CheckCircle, Copy, Loader2, AlertCircle, QrCode, CreditCard, MapPin, Search, Eye, EyeOff, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { trackEvent } from '@/context/CartContext';
import { trackCheckoutStarted, trackPaymentInitiated, trackPurchaseCompleted } from '@/utils/analytics';
import { pixelInitiateCheckout } from '@/utils/fbpixel';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let mpInitialized = false;
function ensureMP(publicKey) {
  if (!mpInitialized && publicKey) {
    initMercadoPago(publicKey, { locale: 'pt-BR' });
    mpInitialized = true;
  }
}

const formatCPF = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, e) => e ? `${a}.${b}.${c}-${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a);
};

const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{0,2})(\d{0,4})(\d{0,4})/, (_, a, b, c) => a && b ? `(${a}) ${b}${c ? '-' + c : ''}` : a ? `(${a}` : '');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

const formatCEP = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return d.replace(/(\d{5})(\d{0,3})/, (_, a, b) => b ? `${a}-${b}` : a);
};

const validateCPF = (cpf) => {
  const c = cpf.replace(/\D/g, '');
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(c[i]) * (10 - i);
  let r = (s * 10) % 11; if (r >= 10) r = 0;
  if (r !== parseInt(c[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(c[i]) * (11 - i);
  r = (s * 10) % 11; if (r >= 10) r = 0;
  return r === parseInt(c[10]);
};

function Field({ label, error, valid, children, required }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
        {label}{required && <span className="text-gray-900 ml-0.5">*</span>}
        {valid && <CheckCircle className="h-3.5 w-3.5 text-green-600" />}
      </label>
      {children}
      {error && <p className="text-blue-700 text-xs mt-1 font-medium">{error}</p>}
    </div>
  );
}

function CustomerForm({ onNext, initialData }) {
  const [form, setForm] = useState(initialData || {
    first_name: '', last_name: '', email: '', cpf: '', phone: '',
    cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: ''
  });
  const [errors, setErrors] = useState({});
  const [cepLoading, setCepLoading] = useState(false);

  // Check if a field value is valid (for green checkmark)
  const isValid = (field) => {
    const v = form[field];
    if (!v || (typeof v === 'string' && !v.trim())) return false;
    if (errors[field]) return false;
    switch (field) {
      case 'email': return /^[\w.+-]+@[\w.-]+\.\w{2,}$/.test(v);
      case 'cpf': return validateCPF(v);
      case 'phone': return v.replace(/\D/g, '').length >= 10;
      case 'cep': return v.replace(/\D/g, '').length === 8;
      case 'state': return v.length === 2;
      default: return v.trim().length > 0;
    }
  };

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors(e => { const n = { ...e }; delete n[field]; return n; });
    }
  };

  const lookupCEP = async (cep) => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      let result = null;

      // Try BrasilAPI first
      try {
        const { data } = await axios.get(`https://brasilapi.com.br/api/cep/v1/${clean}`);
        if (data.city) {
          result = { logradouro: data.street || '', bairro: data.neighborhood || '', localidade: data.city, uf: data.state };
        }
      } catch { /* fallback */ }

      // Try ViaCEP as fallback
      if (!result) {
        try {
          const { data } = await axios.get(`https://viacep.com.br/ws/${clean}/json/`);
          if (!data.erro) result = data;
        } catch { /* fallback */ }
      }

      // Try OpenCEP as third fallback
      if (!result) {
        try {
          const { data } = await axios.get(`https://opencep.com/v1/${clean}`);
          if (data.localidade) result = data;
        } catch { /* continue */ }
      }

      if (result) {
        setForm(f => ({
          ...f,
          street: result.logradouro || f.street,
          neighborhood: result.bairro || f.neighborhood,
          city: result.localidade || f.city,
          state: result.uf || f.state
        }));
        setErrors(e => {
          const n = { ...e };
          delete n.cep;
          if (result.logradouro) delete n.street;
          if (result.bairro) delete n.neighborhood;
          if (result.localidade) delete n.city;
          if (result.uf) delete n.state;
          return n;
        });
      } else {
        setErrors(e => ({ ...e, cep: 'CEP não encontrado' }));
      }
    } catch {
      setErrors(e => ({ ...e, cep: 'Erro ao buscar CEP' }));
    } finally {
      setCepLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Obrigatório';
    if (!form.last_name.trim()) e.last_name = 'Obrigatório';
    if (!form.email.match(/^[\w.+-]+@[\w.-]+\.\w{2,}$/)) e.email = 'Email inválido';
    if (!validateCPF(form.cpf)) e.cpf = 'CPF inválido';
    if (form.phone.replace(/\D/g, '').length < 10) e.phone = 'Telefone inválido';
    if (form.cep.replace(/\D/g, '').length !== 8) e.cep = 'CEP inválido';
    if (!form.street.trim()) e.street = 'Obrigatório';
    if (!form.number.trim()) e.number = 'Obrigatório';
    if (!form.neighborhood.trim()) e.neighborhood = 'Obrigatório';
    if (!form.city.trim()) e.city = 'Obrigatório';
    if (!form.state.trim()) e.state = 'Obrigatório';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onNext(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Dados pessoais</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome" error={errors.first_name} valid={isValid('first_name')} required>
              <Input value={form.first_name} onChange={e => set('first_name', e.target.value)}
                placeholder="João" className={errors.first_name ? 'border-blue-500' : ''} data-testid="checkout-first-name" />
            </Field>
            <Field label="Sobrenome" error={errors.last_name} valid={isValid('last_name')} required>
              <Input value={form.last_name} onChange={e => set('last_name', e.target.value)}
                placeholder="Silva" className={errors.last_name ? 'border-blue-500' : ''} data-testid="checkout-last-name" />
            </Field>
          </div>
          <Field label="Email" error={errors.email} valid={isValid('email')} required>
            <Input type="email" value={form.email} onChange={e => set('email', e.target.value)}
              placeholder="joao@email.com" className={errors.email ? 'border-blue-500' : ''} data-testid="checkout-email" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CPF" error={errors.cpf} valid={isValid('cpf')} required>
              <Input value={form.cpf} onChange={e => set('cpf', formatCPF(e.target.value))}
                placeholder="000.000.000-00" maxLength={14} className={errors.cpf ? 'border-blue-500' : ''} data-testid="checkout-cpf" />
            </Field>
            <Field label="WhatsApp / Telefone" error={errors.phone} valid={isValid('phone')} required>
              <Input value={form.phone} onChange={e => set('phone', formatPhone(e.target.value))}
                placeholder="(11) 99999-9999" maxLength={15} className={errors.phone ? 'border-blue-500' : ''} data-testid="checkout-phone" />
            </Field>
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1">
          <MapPin className="h-3 w-3" /> Endereço de entrega
        </p>
        <div className="space-y-3">
          <Field label="CEP" error={errors.cep} valid={isValid('cep')} required>
            <div className="flex gap-2">
              <Input
                value={form.cep}
                onChange={e => { const v = formatCEP(e.target.value); set('cep', v); if (v.replace(/\D/g,'').length === 8) lookupCEP(v); }}
                placeholder="00000-000"
                maxLength={9}
                className={errors.cep ? 'border-blue-500' : ''}
                data-testid="checkout-cep"
              />
              <Button type="button" variant="outline" size="icon" onClick={() => lookupCEP(form.cep)} disabled={cepLoading} className="shrink-0">
                {cepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </Field>

          <Field label="Rua / Logradouro" error={errors.street} valid={isValid('street')} required>
            <Input value={form.street} onChange={e => set('street', e.target.value)}
              placeholder="Rua das Flores" className={errors.street ? 'border-blue-500' : ''} data-testid="checkout-street" />
          </Field>

          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-2">
              <Field label="Número" error={errors.number} valid={isValid('number')} required>
                <Input value={form.number} onChange={e => set('number', e.target.value)}
                  placeholder="123" className={errors.number ? 'border-blue-500' : ''} data-testid="checkout-number" />
              </Field>
            </div>
            <div className="col-span-3">
              <Field label="Complemento" error={errors.complement} valid={form.complement?.trim().length > 0}>
                <Input value={form.complement} onChange={e => set('complement', e.target.value)}
                  placeholder="Apto 4B (opcional)" data-testid="checkout-complement" />
              </Field>
            </div>
          </div>

          <Field label="Bairro" error={errors.neighborhood} valid={isValid('neighborhood')} required>
            <Input value={form.neighborhood} onChange={e => set('neighborhood', e.target.value)}
              placeholder="Centro" className={errors.neighborhood ? 'border-blue-500' : ''} data-testid="checkout-neighborhood" />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Cidade" error={errors.city} valid={isValid('city')} required>
                <Input value={form.city} onChange={e => set('city', e.target.value)}
                  placeholder="São Paulo" className={errors.city ? 'border-blue-500' : ''} data-testid="checkout-city" />
              </Field>
            </div>
            <Field label="UF" error={errors.state} valid={isValid('state')} required>
              <Input value={form.state} onChange={e => set('state', e.target.value.toUpperCase().slice(0,2))}
                placeholder="SP" maxLength={2} className={errors.state ? 'border-blue-500' : ''} data-testid="checkout-state" />
            </Field>
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full bg-green-800 hover:bg-green-900 text-white py-5 font-semibold rounded-lg mt-2"
        data-testid="checkout-next-btn">
        Continuar para Pagamento
      </Button>
    </form>
  );
}

function CardPaymentStep({ amount, customerData, onSuccess, onError }) {
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (formData) => {
    setProcessing(true);
    try {
      const cpfClean = customerData.cpf.replace(/\D/g, '');
      const response = await axios.post(`${API}/payments/card`, {
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: formData.installments || 1,
        transaction_amount: amount,
        payer_email: customerData.email,
        payer_first_name: customerData.first_name,
        payer_last_name: customerData.last_name,
        payer_identification_type: 'CPF',
        payer_identification_number: cpfClean
      });
      const { status, status_detail, payment_id } = response.data;
      const msgs = {
        'cc_rejected_bad_filled_card_number': 'Número do cartão incorreto.',
        'cc_rejected_bad_filled_security_code': 'CVV incorreto.',
        'cc_rejected_bad_filled_date': 'Data de validade incorreta.',
        'cc_rejected_other_reason': 'Cartão recusado. Use o nome APRO no titular para testar.',
        'cc_rejected_insufficient_amount': 'Saldo insuficiente.',
        'cc_rejected_high_risk': 'Transação recusada por segurança.',
        'cc_rejected_card_disabled': 'Cartão desativado.',
        'cc_rejected_duplicated_payment': 'Pagamento duplicado.',
      };
      if (status === 'approved' || status === 'in_process' || status === 'pending') {
        onSuccess({ payment_id, status, method: 'card' });
      } else {
        onError(`Pagamento recusado — ${msgs[status_detail] || status_detail || 'motivo desconhecido'}`);
      }
    } catch (err) {
      onError(err.response?.data?.detail || 'Erro ao processar. Tente novamente.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <CreditCard className="h-4 w-4" /> Dados do cartão
      </h3>
      <div className={`relative ${processing ? 'opacity-60 pointer-events-none' : ''}`}>
        <CardPayment
          initialization={{ amount }}
          onSubmit={handleSubmit}
          onReady={() => {}}
          onError={() => onError('Erro ao carregar formulário de pagamento.')}
          customization={{ paymentMethods: { minInstallments: 1, maxInstallments: 12 } }}
        />
        {processing && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-green-800" />
          </div>
        )}
      </div>
    </div>
  );
}

function PixPaymentStep({ amount, customerData, onSuccess, onError }) {
  const [loading, setLoading] = useState(true);
  const [pixData, setPixData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [polling, setPolling] = useState(false);

  const generatePix = useCallback(async () => {
    setLoading(true);
    try {
      const cpfClean = customerData.cpf.replace(/\D/g, '');
      const resp = await axios.post(`${API}/payments/pix`, {
        transaction_amount: amount,
        payer_email: customerData.email,
        payer_first_name: customerData.first_name,
        payer_last_name: customerData.last_name,
        payer_identification_type: 'CPF',
        payer_identification_number: cpfClean
      });
      setPixData(resp.data);
      setPolling(true);
    } catch (err) {
      onError(err.response?.data?.detail || 'Erro ao gerar PIX. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [amount, customerData, onError]);

  useEffect(() => {
    if (!polling || !pixData?.payment_id) return;
    const interval = setInterval(async () => {
      try {
        const resp = await axios.get(`${API}/payments/status/${pixData.payment_id}`);
        if (resp.data.status === 'approved') {
          clearInterval(interval);
          setPolling(false);
          onSuccess({ payment_id: pixData.payment_id, status: 'approved', method: 'pix' });
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, pixData, onSuccess]);

  useEffect(() => { generatePix(); }, [generatePix]);

  const copyCode = () => {
    if (!pixData?.qr_code) return;
    navigator.clipboard.writeText(pixData.qr_code);
    setCopied(true);
    toast.success('Código PIX copiado!');
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-green-800" />
      <p className="text-sm text-gray-600">Gerando seu PIX...</p>
    </div>
  );

  if (!pixData) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <QrCode className="h-4 w-4" /> Pagamento via PIX
      </h3>
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-green-800">R$ {amount.toFixed(2).replace('.', ',')}</p>
        <p className="text-xs text-green-700 mt-1">5% de desconto PIX aplicado</p>
      </div>
      {pixData.qr_code_base64 && (
        <div className="flex justify-center">
          <div className="bg-white border-2 border-gray-200 rounded-xl p-3 inline-block">
            <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" className="w-48 h-48" data-testid="pix-qrcode" />
          </div>
        </div>
      )}
      {pixData.qr_code && (
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">Ou copie o código:</p>
          <div className="flex gap-2">
            <Input value={pixData.qr_code} readOnly className="text-xs font-mono bg-gray-50" data-testid="pix-code" />
            <Button onClick={copyCode} variant="outline" className="shrink-0 gap-2" data-testid="pix-copy-btn">
              {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copiado!' : 'Copiar'}
            </Button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
        <Loader2 className="h-4 w-4 animate-spin text-green-700 shrink-0" />
        <span>Aguardando confirmação do pagamento...</span>
      </div>
      <div className="text-xs text-gray-500 space-y-1">
        <p>1. Abra o app do seu banco</p>
        <p>2. Escaneie o QR Code ou cole o código</p>
        <p>3. Confirme o pagamento</p>
        <p>4. A confirmação é automática em segundos</p>
      </div>
    </div>
  );
}

function CreateAccountCTA({ email, totalPaid }) {
  const { user, login } = useAuth();
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // If already logged in, don't show the CTA
  if (user) return null;
  if (!email) return null;

  if (done) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 w-full" data-testid="account-created-msg">
        <div className="flex items-start gap-2">
          <CheckCircle className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-left">
            <p className="text-sm font-semibold text-emerald-800">Conta criada com sucesso!</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              Você ganhou <strong>R$ 20,00 de cashback</strong> de boas-vindas. Agora pode acompanhar seu pedido na área "Minha Conta".
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!password || password.length < 6) {
      setError('A senha precisa ter pelo menos 6 caracteres');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register-from-purchase`, {
        email,
        password,
      });
      if (data?.access_token && data?.user) {
        login(data.user, data.access_token);
      }
      setDone(true);
      toast.success('Conta criada! R$ 20,00 de cashback creditados.');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao criar conta';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 rounded-lg p-4 w-full text-left" data-testid="create-account-cta">
      <div className="flex items-start gap-2 mb-3">
        <UserPlus className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-gray-900">Crie sua conta em 1 clique</p>
          <p className="text-xs text-gray-700 mt-0.5">
            Acompanhe seu pedido e ganhe <strong className="text-emerald-700">R$ 20,00 de cashback</strong> de boas-vindas.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="bg-white rounded-md px-3 py-2 border border-gray-200">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Email do pedido</p>
          <p className="text-xs font-medium text-gray-800 truncate">{email}</p>
        </div>
        <div className="relative">
          <Input
            type={showPwd ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
            placeholder="Crie uma senha (mín. 6 caracteres)"
            className="pr-10 text-sm"
            data-testid="create-account-password"
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          />
          <button
            type="button"
            onClick={() => setShowPwd(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
        <Button
          onClick={handleCreate}
          disabled={loading || !password}
          className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-md py-2 text-sm font-semibold"
          data-testid="btn-create-account"
        >
          {loading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Criando conta...</> : 'Criar minha conta + ganhar R$ 20'}
        </Button>
        <p className="text-[10px] text-gray-500 text-center">
          Usamos os dados do seu pedido. Você pode editar depois em "Minha Conta".
        </p>
      </div>
    </div>
  );
}

function SuccessStep({ paymentInfo, onClose, totalPaid, customerEmail }) {
  return (
    <div className="flex flex-col items-center text-center py-6 gap-4">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="h-10 w-10 text-green-600" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">Pedido Confirmado!</h3>
        <p className="text-sm text-gray-600 mt-1">
          {paymentInfo.method === 'pix' ? 'PIX confirmado com sucesso!' :
            paymentInfo.status === 'approved' ? 'Pagamento aprovado!' : 'Pagamento em análise.'}
        </p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 w-full text-left space-y-1">
        <p className="text-xs text-gray-500">ID do pedido</p>
        <p className="text-sm font-mono font-medium text-gray-800">{paymentInfo.payment_id}</p>
      </div>

      {/* CTA: Create account if guest checkout */}
      <CreateAccountCTA email={customerEmail} totalPaid={totalPaid} />

      <p className="text-xs text-gray-500">Em breve entraremos em contato para confirmar o envio.</p>
      <Button onClick={onClose} className="w-full bg-green-800 hover:bg-green-900 text-white rounded-lg py-5 font-semibold"
        data-testid="checkout-success-close">
        Concluir
      </Button>
    </div>
  );
}

export default function CheckoutModal({ isOpen, onClose, finalAmount, pixAmount, paymentMethod, cartItems, couponCode, couponDiscount, subtotal, cashbackUsed }) {
  const [step, setStep] = useState('customer');
  const [customerData, setCustomerData] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [mpPublicKey, setMpPublicKey] = useState('');
  const { user } = useAuth();

  // Pre-fill form when user is logged in (and form not yet filled)
  useEffect(() => {
    if (isOpen && user && !customerData) {
      const fullName = (user.name || '').trim();
      const parts = fullName.split(/\s+/);
      const firstName = parts.shift() || '';
      const lastName = parts.join(' ');
      const defaultAddr = (user.addresses || []).find(a => a.is_default) || (user.addresses || [])[0] || {};
      setCustomerData({
        first_name: firstName,
        last_name: lastName,
        email: user.email || '',
        cpf: user.cpf || '',
        phone: user.phone || '',
        cep: defaultAddr.cep || '',
        street: defaultAddr.street || '',
        number: defaultAddr.number || '',
        complement: defaultAddr.complement || '',
        neighborhood: defaultAddr.neighborhood || '',
        city: defaultAddr.city || '',
        state: defaultAddr.state || '',
      });
    }
  }, [isOpen, user, customerData]);

  useEffect(() => {
    if (!isOpen) return;
    const key = process.env.REACT_APP_MP_PUBLIC_KEY;
    if (key) { setMpPublicKey(key); ensureMP(key); return; }
    axios.get(`${API}/payments/public-key`).then(r => {
      if (r.data.public_key) { setMpPublicKey(r.data.public_key); ensureMP(r.data.public_key); }
    }).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) { setStep('customer'); setErrorMsg(''); setPaymentInfo(null); }
  }, [isOpen]);

  const saveOrder = async (info, customer) => {
    try {
      await axios.post(`${API}/orders/from-payment`, {
        customer_name: `${customer.first_name} ${customer.last_name}`,
        customer_email: customer.email,
        customer_phone: customer.phone,
        customer_cpf: customer.cpf.replace(/\D/g, ''),
        address_cep: customer.cep.replace(/\D/g, ''),
        address_street: customer.street,
        address_number: customer.number,
        address_complement: customer.complement || '',
        address_neighborhood: customer.neighborhood,
        address_city: customer.city,
        address_state: customer.state,
        items: (cartItems || []).map(i => ({
          product_id: i.product_id,
          name: i.name,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
          price: i.price
        })),
        subtotal: subtotal || null,
        total: info.method === 'pix' ? pixAmount : finalAmount,
        payment_id: info.payment_id,
        payment_method: info.method,
        payment_status: info.status,
        coupon_code: couponCode || null,
        coupon_discount: couponDiscount || null,
        cashback_used: cashbackUsed || 0
      });
    } catch (e) {
      console.error('Erro ao salvar pedido:', e);
    }
  };

  const handleCustomerNext = (data) => {
    setCustomerData(data);
    setStep('payment');
    setErrorMsg('');
    trackEvent('start_checkout', {
      email: data.email,
      name: `${data.first_name} ${data.last_name}`,
      items: (cartItems || []).map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity })),
      total: paymentMethod === 'pix' ? pixAmount : finalAmount
    });
    trackCheckoutStarted(paymentMethod === 'pix' ? pixAmount : finalAmount, (cartItems || []).reduce((s, i) => s + i.quantity, 0));
    trackPaymentInitiated(paymentMethod, paymentMethod === 'pix' ? pixAmount : finalAmount);
    pixelInitiateCheckout(cartItems || [], paymentMethod === 'pix' ? pixAmount : finalAmount);
  };

  const handlePaymentSuccess = async (info) => {
    setPaymentInfo(info);
    trackEvent('payment_completed', {
      payment_id: info.payment_id,
      method: info.method,
      status: info.status,
      total: paymentMethod === 'pix' ? pixAmount : finalAmount
    });
    trackPurchaseCompleted(
      info.payment_id,
      paymentMethod === 'pix' ? pixAmount : finalAmount,
      info.method,
      null,
      cartItems || []
    );
    await saveOrder(info, customerData);
    setStep('success');
  };

  if (!isOpen) return null;
  const amount = paymentMethod === 'pix' ? pixAmount : finalAmount;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="checkout-modal-overlay" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] md:max-h-[90vh] overflow-y-auto mb-16 md:mb-0" data-testid="checkout-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-green-700" />
            <span className="text-sm font-semibold text-gray-900">
              {step === 'customer' && 'Finalizar Compra'}
              {step === 'payment' && `Pagamento — R$ ${amount.toFixed(2).replace('.', ',')}`}
              {step === 'success' && 'Pedido Confirmado'}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-testid="checkout-modal-close">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {step !== 'success' && (
          <div className="flex items-center gap-2 px-6 pt-4 pb-1">
            {['customer', 'payment'].map((s, i) => (
              <div key={s} className="flex items-center gap-1.5">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold
                  ${step === s ? 'bg-green-800 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
                <span className={`text-xs font-medium ${step === s ? 'text-green-800' : 'text-gray-400'}`}>
                  {s === 'customer' ? 'Dados' : 'Pagamento'}
                </span>
                {i === 0 && <div className="w-6 h-px bg-gray-200 mx-1" />}
              </div>
            ))}
          </div>
        )}

        <div className="px-6 py-4 pb-20 md:pb-4">
          {errorMsg && (
            <div className="mb-4 flex items-start gap-2 bg-blue-50 border border-blue-500 rounded-lg p-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold leading-none">i</span>
              </div>
              <p className="text-sm text-blue-800">{errorMsg}</p>
            </div>
          )}
          {step === 'customer' && <CustomerForm onNext={handleCustomerNext} initialData={customerData} />}
          {step === 'payment' && paymentMethod === 'credit' && (
            mpPublicKey
              ? <CardPaymentStep amount={amount} customerData={customerData} onSuccess={handlePaymentSuccess} onError={setErrorMsg} />
              : <div className="flex flex-col items-center py-12 gap-3"><Loader2 className="h-8 w-8 animate-spin text-green-800" /><p className="text-sm text-gray-600">Carregando...</p></div>
          )}
          {step === 'payment' && paymentMethod === 'pix' && (
            <PixPaymentStep amount={amount} customerData={customerData} onSuccess={handlePaymentSuccess} onError={setErrorMsg} />
          )}
          {step === 'success' && <SuccessStep paymentInfo={paymentInfo} onClose={onClose} totalPaid={amount} customerEmail={customerData?.email} />}
        </div>

        {step !== 'success' && (
          <div className="px-6 pb-4 text-center">
            <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1">
              <Lock className="h-3 w-3" />
              Pagamento 100% seguro via Mercado Pago · SSL 256-bit
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
